/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import * as React from 'react';
import { useEffect, useState } from 'react';

import FocusTrap from 'focus-trap-react';

import { useRetracked } from 'js/lib/retracked';

import { IconButton } from '@coursera/cds-core';
import { track } from '@coursera/event-pulse/sdk';

import MobileNavigationContext from 'bundles/composable-page-header/contexts/MobileNavigationContext';
import { impressShowNewMegamenu, shouldShowNewMegamenu } from 'bundles/megamenu/utils/experimentUtils';

import _t from 'i18n!nls/composable-page-header';

type Props = {
  menuConfig: React.ReactElement;
  homeLogo: React.ReactElement;
};

const styles = {
  menuButton: css`
    color: black;
    margin-right: var(--cds-spacing-100);
  `,
  button: css`
    color: black;
  `,
  fixedHeader: css`
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    width: 100%;
    height: var(--cds-spacing-800);
    border-bottom: 1px solid var(--cds-color-grey-100);
    display: flex;
    align-items: center;
    justify-content: flex-end;
    padding: 0 var(--cds-spacing-100);
  `,
  centeredLogo: css`
    position: absolute;
    left: 50%;
    transform: translateX(-50%);
  `,
  closeButton: css`
    display: flex;
    align-items: center;
    justify-content: flex-end;
  `,
  menuConfig: css`
    position: fixed;
    top: var(--cds-spacing-800); /* below the header */
    left: 0;
    right: 0;
    bottom: 0;
    overflow-y: auto;
    padding: 0 var(--cds-spacing-200);

    :has(> .mobile-auth-menu) {
      padding-bottom: 150px;
    }
  `,
  background: (open: boolean) => css`
    background-color: white;
    z-index: 1000;
    width: 100vw;
    height: 100%;
    position: fixed;
    top: 0;
    left: 0;
    transition: transform 0.3s ease;
    transform: ${!open ? 'translateX(-100%)' : 'translateX(0)'};
  `,
};

const MobileMenuIconGridItem = ({ menuConfig, homeLogo }: Props) => {
  const [isOpen, setIsOpen] = useState(false);
  const trackEvent = useRetracked();

  const closeMenu = () => {
    setIsOpen(false);
  };

  const openMenu = () => {
    impressShowNewMegamenu();
    if (shouldShowNewMegamenu()) {
      track('open_navigation_menu', {
        navigationType: 'megamenu',
      });
    }
    setIsOpen(true);
  };

  const handleKeyDown = (event: KeyboardEvent) => {
    if (event.key === 'Escape') {
      setIsOpen(false);
    }
  };

  useEffect(() => {
    document.body.style.overflow = isOpen ? 'hidden' : 'unset';

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  return (
    <nav>
      <IconButton
        size="medium"
        intent="menu"
        variant="ghost"
        onClick={() => {
          trackEvent({
            trackingName: `header_right_nav_button`,
            action: 'click',
            trackingData: {
              name: 'open-nav',
            },
          });
          openMenu();
        }}
        css={styles.menuButton}
        className="mobile-menu-hamburger"
        aria-label={_t('Open Navigation Menu')}
      />
      {isOpen && (
        <MobileNavigationContext.Provider value={{ closeMobileMenu: closeMenu }}>
          <FocusTrap
            focusTrapOptions={{
              allowOutsideClick: false,
              initialFocus: document.querySelector('.mobile-header-logo a') as HTMLElement,
            }}
          >
            <div css={styles.background(isOpen)}>
              <div css={styles.fixedHeader}>
                <div css={styles.centeredLogo} className="mobile-header-logo">
                  {homeLogo}
                </div>
                <div css={styles.closeButton}>
                  <IconButton size="medium" intent="close" variant="ghost" onClick={closeMenu} css={styles.button} />
                </div>
              </div>
              <ul css={styles.menuConfig} className="menuConfig">
                {menuConfig}
              </ul>
            </div>
          </FocusTrap>
        </MobileNavigationContext.Provider>
      )}
    </nav>
  );
};

export default MobileMenuIconGridItem;
